import {
  CalendarPrimeReact,
  DropdownPrimeReact,
  InputCurrency,
  InputCurrencyGroup,
} from "@finanedi.es/storybook"
import { TFunction } from "i18next"
import { Checkbox } from "primereact/checkbox"
import { InputNumber } from "primereact/inputnumber"
import { InputText } from "primereact/inputtext"
import { FieldError, UseFormSetValue } from "react-hook-form"

export interface FieldTemplateOne {
  label: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  code: any
  value: number | string | Date | null | undefined | boolean
  disabled?: boolean
  inputNumber?: string
  colSpan?: string
  inputType?:
    | "calendar"
    | "checkbox"
    | "currency"
    | "currencyGroup"
    | "dropdown"
    | "number"
    | "numberGroup"
    | "text"
  options?: { label: string; value: string | number }[]
  mandatory?: boolean
  className?: string
  onChange?: (value: number | string | boolean | Date) => void

  error?: FieldError | string
}

export interface FieldTemplateThree {
  section: string
  labelOne?: string
  inputsOne: FieldTemplateOne[]
  labelTwo?: string
  inputsTwo?: FieldTemplateOne[]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getInputType = (field: FieldTemplateOne, setValue: any) => {
  const INPUT_TYPE = {
    calendar: (
      <CalendarPrimeReact
        value={field.value instanceof Date ? field.value : undefined}
        selectionMode='single'
        dateFormat='dd/mm/yy'
        className={`tw-w-full ${field.error ? "!tw-border-danger" : ""}`}
        onChange={({ value }) => {
          setValue(`${field.code}`, value)
        }}
        readOnlyInput
        showIcon
      />
    ),
    checkbox: (
      <Checkbox
        checked={field.value}
        onChange={() => setValue(`${field.code}`, !field.value)}
        disabled={field.disabled ?? false}
      />
    ),
    currency: (
      <InputCurrency
        placeholder='0,00'
        value={typeof field.value === "number" ? field.value : 0}
        className='!tw-w-full !tw-h-10 tw-font-normal tw-text-sm'
        suffix='€'
        onChange={(value) => setValue(`${field.code}`, value)}
      />
    ),
    currencyGroup: (
      <InputCurrencyGroup
        suffix='€'
        value={typeof field.value === "number" ? field.value : 0}
        onChange={(value) => setValue(`${field.code}`, value ?? 0)}
        placeholder='0,00'
        className='tw-w-full tw-h-full tw-text-sm'
        numberGroup={field?.inputNumber ? field.inputNumber : ""}
        disabled={field.disabled ?? false}
      />
    ),
    dropdown: (
      <DropdownPrimeReact
        value={field.value}
        options={field.options}
        optionLabel='label'
        optionValue='value'
        onChange={(e) => setValue(`${field.code}`, e.value)}
        placeholder='Selecciona'
        className='!tw-w-full !tw-h-10 [&_span]:tw-text-sm'
        panelClassName='tw-rounded-md tw-max-w-100 tw-text-sm'
        disabled={field.disabled ?? false}
      />
    ),
    number: (
      <InputNumber
        value={typeof field.value === "number" ? field.value : 0}
        placeholder='0'
        className='!tw-w-full !tw-h-10 !tw-rounded [&_input]:!tw-w-full [&_input]:tw-text-sm [&_input]:!tw-text-right'
        onChange={(e) => setValue(`${field.code}`, e.value)}
      />
    ),
    numberGroup: (
      <div className='p-inputgroup tw-flex-1 tw-w-full'>
        <span className='p-inputgroup-addon tw-flex tw-items-center tw-px-3 tw-bg-[#E9ECEF] tw-border tw-border-r-0 tw-border-gray-300 tw-rounded-tl-md tw-rounded-bl-md tw-text-sm tw-text-[#6C757D]'>
          {field.code}
        </span>
        <div className='tw-flex-1 tw-min-w-0'>
          <InputNumber
            value={typeof field.value === "number" ? field.value : 0}
            onChange={(e) => setValue(`${field.code}`, e.value ?? 0)}
            placeholder='0'
            disabled={field.disabled ?? false}
            className={`!tw-w-full [&_input]:!tw-h-10 [&_input]:tw-text-sm [&_input]:!tw-text-right [&_input]:!tw-rounded-tl-none [&_input]:!tw-rounded-bl-none [&_input]:!tw-rounded-tr-md [&_input]:!tw-rounded-br-md [&_input]:!tw-border-l-0 ${
              field.disabled ? "[&_input]:!tw-text-gray-400" : ""
            }`}
          />
        </div>
      </div>
    ),
    text: (
      <InputText
        className={`tw-w-full tw-text-sm ${
          field.className ? field.className : ""
        } ${field.error ? "!tw-border-danger" : ""}`}
        value={typeof field.value === "string" ? field.value : ""}
        onChange={(e) => {
          if (field.onChange) {
            field.onChange(e.target.value)
          } else {
            setValue(`${field.code}`, e.target.value)
          }
        }}
        disabled={field.disabled ?? false}
      />
    ),
  }
  return field?.inputType
    ? INPUT_TYPE[field.inputType]
    : INPUT_TYPE.currencyGroup
}

export const renderFieldTemplateOne = (
  field: FieldTemplateOne,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>,
  colSpanWrapper = "tw-grid-cols-5",
  textColSpan = "tw-col-span-4",
  inputColSpan = "tw-col-span-1"
) => (
  <div
    key={field.code}
    className={`tw-grid ${colSpanWrapper} tw-items-center tw-gap-2 tw-border-b tw-border-gray-200 tw-py-3`}
  >
    <div className={`${textColSpan} tw-text-sm`}>{field.label}</div>
    <div className={`${inputColSpan}`}>{getInputType(field, setValue)}</div>
  </div>
)

export interface FieldTemplateTwo {
  label: string
  inputsOne: FieldTemplateOne[]
}

export const renderFieldTemplateTwo = (
  field: FieldTemplateTwo,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>,
  colSpanWrapper = "tw-grid-cols-5",
  textColSpan = "tw-col-span-3",
  inputColSpan = "tw-col-span-1"
) => (
  <div
    key={field.label}
    className={`tw-w-full tw-h-fit tw-grid ${colSpanWrapper} tw-py-3 tw-gap-2 tw-border-b tw-border-neutral-25 tw-text-center`}
  >
    {/**  columna 2: label de la fila */}
    <div
      className={`tw-text-sm ${textColSpan} tw-text-left tw-flex tw-items-center`}
    >
      {field.label}
    </div>
    {/**  columna 3 y 4: inputs (base / cuota) */}
    {field.inputsOne.map((input) => (
      <div
        key={input.inputNumber}
        className={`${inputColSpan}`}
      >
        <InputCurrencyGroup
          suffix='€'
          value={typeof input.value === "number" ? input.value : 0}
          onChange={(value) => setValue(`${input.code}`, value ?? 0)}
          placeholder='0,00'
          className='tw-h-full tw-text-sm'
          numberGroup={input?.inputNumber ? input.inputNumber : ""}
          disabled={input.disabled ?? false}
        />
      </div>
    ))}
  </div>
)

export const TEMPLATE_TYPE = {
  TEMPLATE_FORMAT_ONE: 1,
  TEMPLATE_FORMAT_TWO: 2,
  TEMPLATE_FORMAT_THREE: 3,
}

export const renderFieldTemplateThree = (
  field: FieldTemplateThree,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>,
  templateType = TEMPLATE_TYPE.TEMPLATE_FORMAT_ONE,
  colSpanWrapper = "tw-grid-cols-5",
  textColSpan = "tw-col-span-2 tw-row-span-2",
  inputColSpan = "tw-col-span-1"
) => {
  const colGrid = field.inputsOne.length
  return (
    <div
      key={field.section}
      className={`tw-w-full tw-h-fit tw-grid ${colSpanWrapper} ${
        templateType === TEMPLATE_TYPE.TEMPLATE_FORMAT_ONE
          ? "tw-gap-2"
          : "tw-gap-x-5"
      } tw-border-b tw-border-gray-200 tw-py-3 tw-items-center !tw-grid-cols-${colGrid}`}
    >
      {templateType === TEMPLATE_TYPE.TEMPLATE_FORMAT_THREE ? (
        <div
          className={`tw-col-span-${colGrid} tw-text-sm tw-leading-relaxed tw-border-b tw-border-gray-200 tw-pb-3 ${
            templateType === TEMPLATE_TYPE.TEMPLATE_FORMAT_THREE
              ? "tw-font-normal"
              : "tw-font-medium"
          } `}
        >
          {field.section}
        </div>
      ) : (
        <div className={`${textColSpan} tw-text-sm tw-leading-relaxed`}>
          {field.section}
        </div>
      )}
      {templateType === TEMPLATE_TYPE.TEMPLATE_FORMAT_ONE && (
        <div className={`${inputColSpan} tw-text-sm`}>{field?.labelOne}</div>
      )}
      {field?.inputsOne?.map((input) => {
        if (
          templateType === TEMPLATE_TYPE.TEMPLATE_FORMAT_TWO ||
          templateType === TEMPLATE_TYPE.TEMPLATE_FORMAT_THREE
        ) {
          return (
            <div
              key={input.code}
              className={`${
                input?.colSpan ? input.colSpan : inputColSpan
              } tw-text-sm`}
            >
              <div className='tw-text-left tw-text-xs tw-font-medium'>
                {input.label}
              </div>
              {getInputType(input, setValue)}
            </div>
          )
        }

        return (
          <div
            key={input.inputNumber}
            className={`${inputColSpan}`}
          >
            <InputCurrencyGroup
              suffix='€'
              value={(input.value as number) ?? 0}
              onChange={(value) => setValue(`${input.code}`, value ?? 0)}
              placeholder='0,00'
              className='tw-h-full tw-text-sm'
              numberGroup={input?.inputNumber ? input.inputNumber : ""}
              disabled={input?.disabled ?? false}
            />
          </div>
        )
      })}
      {field?.labelTwo ? (
        <div
          className={`tw-col-span-1 tw-text-sm ${
            TEMPLATE_TYPE.TEMPLATE_FORMAT_THREE
              ? "tw-h-full tw-flex tw-items-center tw-pt-3"
              : ""
          }`}
        >
          {field?.labelTwo}
        </div>
      ) : null}
      {field?.inputsTwo?.map((input) => {
        if (
          templateType === TEMPLATE_TYPE.TEMPLATE_FORMAT_TWO ||
          templateType === TEMPLATE_TYPE.TEMPLATE_FORMAT_THREE
        ) {
          return (
            <div
              key={input.code}
              className={`${inputColSpan} tw-text-sm`}
            >
              <div className='tw-text-left tw-text-xs tw-font-medium'>
                {input.label}
              </div>
              {getInputType(input, setValue)}
            </div>
          )
        }
        return (
          <div
            key={input.inputNumber}
            className='tw-col-span-1'
          >
            <InputCurrencyGroup
              suffix='€'
              value={(input.value as number) ?? 0}
              onChange={(value) => setValue(`${input.code}`, value ?? 0)}
              placeholder='0,00'
              className='tw-h-full tw-text-sm'
              numberGroup={input?.inputNumber ? input.inputNumber : ""}
              disabled={input.disabled ?? false}
            />
          </div>
        )
      })}
    </div>
  )
}

/** TEMPLATES SIN NUMBERGROUP GENÉRICAS */

export interface FieldTemplateGeneric {
  id?: string
  columsNumber?: string
  inputsOne: FieldTemplateOne[]
}

export const renderFieldTemplateGeneric = ({
  fields,
  setValue,
  t,
}: {
  fields: FieldTemplateOne[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>
  t?: TFunction
}) =>
  fields.map((field) => (
    <>
      <div
        className={`${
          field?.colSpan ? field.colSpan : "tw-col-span-1"
        } tw-text-sm ${
          field.inputType === "checkbox"
            ? "tw-flex tw-justify-between tw-items-center"
            : ""
        }`}
      >
        <div
          className='tw-text-left tw-text-xs tw-font-medium'
          key={field.label}
        >
          {field.label}
        </div>
        {getInputType(field, setValue)}
        {field?.mandatory ? (
          <small
            className={`${
              field?.error ? "tw-text-danger" : "tw-text-gray-500"
            }`}
          >
            {t && t("taxSummary.declaracion.mandatoryField")}
          </small>
        ) : null}
      </div>
    </>
  ))
